import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGuardarPesos, useMatrizPesos } from '@/hooks/usePesos';
import { cn } from '@/lib/utils';
import type { AmbitoPesos, GrupoPesos, TipoCasa } from '@adelante/shared';
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const TIPOS: TipoCasa[] = ['1N-Techo', '1N-Azotea', '2N-Techo', '2N-Azotea'];
const TOLERANCIA = 0.5;

/** subId → tipo_casa → peso (string para el input) */
type Edicion = Record<number, Partial<Record<TipoCasa, string>>>;

/**
 * Admin · Pesos — una sola pantalla con toggle Sprint / Partida. Cada modo
 * muestra TODOS los grupos a la vez (colapsables); cada grupo debe sumar 100%
 * por tipo de casa.
 *   - Por sprint:  cuánto pesa cada sub-partida dentro de su sprint.
 *   - Por partida: cuánto pesa cada sub-partida dentro de su partida.
 */
export function PesosPantalla() {
  const [ambito, setAmbito] = useState<AmbitoPesos>('sprint');
  const matrizQ = useMatrizPesos(ambito);
  const guardar = useGuardarPesos();
  const grupos = matrizQ.data?.data?.grupos ?? [];

  const [edicion, setEdicion] = useState<Edicion>({});
  const [colapsados, setColapsados] = useState<Set<number>>(new Set());

  // Al llegar la matriz (o cambiar de ámbito): sembrar edición + colapsar nada.
  useEffect(() => {
    const gs = matrizQ.data?.data?.grupos;
    if (!gs) return;
    const init: Edicion = {};
    for (const g of gs) {
      for (const sp of g.sub_partidas) {
        init[sp.id] = {};
        for (const tc of sp.tipos_casa) init[sp.id]![tc] = String(sp.pesos[tc] ?? 0);
      }
    }
    setEdicion(init);
    setColapsados(new Set());
  }, [matrizQ.data]);

  function sumasDe(g: GrupoPesos): Partial<Record<TipoCasa, number>> {
    const s: Partial<Record<TipoCasa, number>> = {};
    for (const tc of TIPOS) {
      let suma = 0;
      let aplica = false;
      for (const sp of g.sub_partidas) {
        if (!sp.tipos_casa.includes(tc)) continue;
        aplica = true;
        const v = Number.parseFloat(edicion[sp.id]?.[tc] ?? '0');
        if (Number.isFinite(v)) suma += v;
      }
      if (aplica) s[tc] = Math.round(suma * 100) / 100;
    }
    return s;
  }

  const okPorGrupo = new Map<number, boolean>();
  for (const g of grupos) {
    const s = sumasDe(g);
    okPorGrupo.set(
      g.clave,
      Object.values(s).every((v) => Math.abs((v ?? 0) - 100) <= TOLERANCIA),
    );
  }
  const todoOk = grupos.length > 0 && [...okPorGrupo.values()].every(Boolean);

  function setPeso(subId: number, tc: TipoCasa, valor: string) {
    setEdicion((prev) => ({ ...prev, [subId]: { ...prev[subId], [tc]: valor } }));
  }
  function toggle(clave: number) {
    setColapsados((prev) => {
      const n = new Set(prev);
      if (n.has(clave)) n.delete(clave);
      else n.add(clave);
      return n;
    });
  }
  const todosColapsados = grupos.length > 0 && colapsados.size === grupos.length;
  function toggleTodos() {
    setColapsados(todosColapsados ? new Set() : new Set(grupos.map((g) => g.clave)));
  }

  async function handleGuardar() {
    const pesos: { sub_partida_id: number; tipo_casa: TipoCasa; peso: number }[] = [];
    for (const g of grupos) {
      for (const sp of g.sub_partidas) {
        for (const tc of sp.tipos_casa) {
          const v = Number.parseFloat(edicion[sp.id]?.[tc] ?? '0');
          pesos.push({ sub_partida_id: sp.id, tipo_casa: tc, peso: Number.isFinite(v) ? v : 0 });
        }
      }
    }
    try {
      await guardar.mutateAsync({ ambito, body: { pesos } });
      toast.success(`Pesos por ${ambito} guardados`);
    } catch (e) {
      toast.error('Error al guardar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pesos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Peso (%) de cada sub-partida dentro de su {ambito === 'sprint' ? 'sprint' : 'partida'},
            por tipo de casa. Cada {ambito} debe sumar 100% por columna.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle ámbito */}
          <div className="flex rounded-md border border-border bg-white p-0.5">
            {(['sprint', 'partida'] as const).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAmbito(a)}
                className={cn(
                  'rounded px-3 py-1 text-sm font-medium capitalize transition-colors',
                  ambito === a
                    ? 'bg-adelante-primary text-adelante-foreground'
                    : 'text-muted-foreground',
                )}
              >
                Por {a}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={toggleTodos} disabled={grupos.length === 0}>
            {todosColapsados ? 'Expandir todos' : 'Colapsar todos'}
          </Button>
          <Button onClick={handleGuardar} disabled={!todoOk || guardar.isPending}>
            {guardar.isPending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Guardando…
              </>
            ) : (
              'Guardar todos los pesos'
            )}
          </Button>
        </div>
      </div>

      {matrizQ.isLoading && (
        <div className="flex items-center gap-2 py-8 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando…
        </div>
      )}
      {matrizQ.error && <p className="py-8 text-destructive">Error: {matrizQ.error.message}</p>}

      {!matrizQ.isLoading &&
        grupos.map((g) => {
          const sumas = sumasDe(g);
          const tiposActivos = TIPOS.filter((tc) => sumas[tc] != null);
          const colapsado = colapsados.has(g.clave);
          const ok = okPorGrupo.get(g.clave) ?? false;
          return (
            <Card key={g.clave} className="overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(g.clave)}
                className="flex w-full items-center justify-between gap-3 border-b border-border bg-zinc-50 px-4 py-2 text-left hover:bg-zinc-100"
              >
                <span className="flex items-center gap-2 text-sm font-semibold">
                  {colapsado ? (
                    <CaretRight className="h-4 w-4" weight="bold" aria-hidden="true" />
                  ) : (
                    <CaretDown className="h-4 w-4" weight="bold" aria-hidden="true" />
                  )}
                  {ambito === 'partida' && <span className="font-mono">{g.codigo}</span>}
                  {g.nombre}
                  <span className="font-normal text-muted-foreground">
                    {g.sub_partidas.length} sub-partidas
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1.5">
                  {tiposActivos.map((tc) => {
                    const v = sumas[tc] ?? 0;
                    const tcOk = Math.abs(v - 100) <= TOLERANCIA;
                    return (
                      <span
                        key={tc}
                        className={cn(
                          'rounded px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums',
                          tcOk
                            ? 'bg-adelante-primary/15 text-adelante-primary-dark'
                            : 'bg-red-100 text-red-700',
                        )}
                      >
                        {tc}: {v.toFixed(1)}%
                      </span>
                    );
                  })}
                  {ok ? (
                    <CheckCircle
                      className="h-4 w-4 text-adelante-primary-dark"
                      weight="fill"
                      aria-hidden="true"
                    />
                  ) : (
                    <WarningCircle
                      className="h-4 w-4 text-red-500"
                      weight="fill"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </button>

              {!colapsado && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs">Código</TableHead>
                      <TableHead>Sub-partida</TableHead>
                      {ambito === 'partida' && (
                        <TableHead className="text-center text-xs">Sprint</TableHead>
                      )}
                      {tiposActivos.map((tc) => (
                        <TableHead key={tc} className="text-center font-mono text-xs">
                          {tc}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.sub_partidas.map((sp) => (
                      <TableRow key={sp.id}>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {sp.codigo}
                        </TableCell>
                        <TableCell className="text-sm">{sp.nombre}</TableCell>
                        {ambito === 'partida' && (
                          <TableCell className="text-center text-xs text-muted-foreground">
                            {sp.sprint_numero}
                          </TableCell>
                        )}
                        {tiposActivos.map((tc) => {
                          const aplica = sp.tipos_casa.includes(tc);
                          return (
                            <TableCell key={tc} className="text-center">
                              {aplica ? (
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  step="0.01"
                                  value={edicion[sp.id]?.[tc] ?? '0'}
                                  onChange={(e) => setPeso(sp.id, tc, e.target.value)}
                                  className="h-8 w-20 text-center tabular-nums"
                                />
                              ) : (
                                <span className="text-xs text-muted-foreground">N/A</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 border-t-border bg-muted/30 font-semibold">
                      <TableCell />
                      <TableCell className="text-sm">TOTAL</TableCell>
                      {ambito === 'partida' && <TableCell />}
                      {tiposActivos.map((tc) => {
                        const v = sumas[tc] ?? 0;
                        const tcOk = Math.abs(v - 100) <= TOLERANCIA;
                        return (
                          <TableCell key={tc} className="text-center">
                            <span
                              className={cn(
                                'tabular-nums',
                                tcOk ? 'text-adelante-primary-dark' : 'text-destructive',
                              )}
                            >
                              {v.toFixed(2)}%
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Card>
          );
        })}

      {!todoOk && grupos.length > 0 && (
        <p className="text-xs text-destructive">
          ⚠️ Algunos {ambito === 'sprint' ? 'sprints' : 'partidas'} no suman 100% por columna.
          Corregí antes de guardar.
        </p>
      )}
    </div>
  );
}
